import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, ChevronLeft, ChevronRight, Star } from "lucide-react";

const TESTIMONIALS = [
  {
    quote:
      "AKR turned around our connection package for a 22-story tower in 11 business days. Shop drawings were mill-ready — our fabricator ran the plates straight to CNC. No RFIs, no re-issues.",
    name: "Michael Whitaker, P.E.",
    role: "Senior Structural Engineer",
    location: "Dallas, TX",
    project: "22-story mixed-use tower",
    stat: "11 days",
    statLabel: "Turnaround",
  },
  {
    quote:
      "We use industry-standard modeling in-house so integration matters. Kranthi's team hands over models that open clean — clash-free, IFC-compatible, correctly labeled. Best detailing partner we've worked with.",
    name: "David Chen",
    role: "VP of Engineering",
    location: "Houston, TX",
    project: "Refinery pipe rack",
    stat: "0",
    statLabel: "Rework tickets",
  },
  {
    quote:
      "AKR handled the entire anchor-bolt plan set for our airport concourse expansion. Every embed lined up on the first pour. Their PE stamps saved us weeks in review.",
    name: "Rebecca Hollis",
    role: "Project Manager",
    location: "Denver, CO",
    project: "Airport concourse",
    stat: "100%",
    statLabel: "First-pour hit rate",
  },
  {
    quote:
      "We priced three detailers. Kranthi came in mid-range but delivered PDF drawings that our field crew could actually read at 3 AM without calling. That's the whole game.",
    name: "Tom Reyes",
    role: "Erection Superintendent",
    location: "Phoenix, AZ",
    project: "Distribution warehouse",
    stat: "24/7",
    statLabel: "Field support",
  },
];

export const Testimonials = () => {
  const [idx, setIdx] = useState(0);
  const current = TESTIMONIALS[idx];

  const goPrev = () => setIdx((i) => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);
  const goNext = () => setIdx((i) => (i + 1) % TESTIMONIALS.length);

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative py-20 md:py-28 border-t border-slate-200/60 bg-white overflow-hidden"
    >
      <div className="relative px-6 md:px-10 lg:px-14 xl:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-12">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 mb-6 text-xs font-mono uppercase tracking-[0.22em] text-slate-500">
              <span className="w-8 h-px bg-[#0A2540]" />
              07 · Testimonials
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold text-[#0A2540] tracking-tight leading-[1.08]">
              What clients say
              <br />
              after they get the{" "}
              <span className="text-slate-400">drawings</span>.
            </h2>
          </div>
          <div className="lg:col-span-5 flex lg:items-end">
            <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-xl">
              Real feedback from GCs, EPC firms, fabricators and erectors across
              the US who&apos;ve trusted us with their steel detailing.
            </p>
          </div>
        </div>

        {/* Testimonial card */}
        <div className="relative bg-slate-50 rounded-3xl border border-slate-200 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12">
            {/* Quote side */}
            <div className="lg:col-span-8 p-8 md:p-12 lg:p-16 relative">
              <Quote
                className="absolute top-6 left-6 w-14 h-14 text-slate-200"
                strokeWidth={1.5}
              />

              <AnimatePresence mode="wait">
                <motion.div
                  key={idx}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="relative"
                >
                  <div className="flex items-center gap-1 mb-6">
                    {[0, 1, 2, 3, 4].map((s) => (
                      <Star
                        key={s}
                        className="w-4 h-4 text-[#0A2540]"
                        strokeWidth={2}
                        fill="#0A2540"
                      />
                    ))}
                  </div>

                  <blockquote className="font-display text-xl md:text-2xl lg:text-3xl text-[#0A2540] leading-[1.35] tracking-tight font-medium">
                    &ldquo;{current.quote}&rdquo;
                  </blockquote>

                  <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
                    <div>
                      <div className="font-display font-semibold text-[#0A2540] text-lg">
                        {current.name}
                      </div>
                      <div className="text-sm text-slate-600">
                        {current.role}
                      </div>
                    </div>
                    <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500 border-l border-slate-200 pl-6">
                      {current.location}
                      <br />
                      <span className="text-slate-400">{current.project}</span>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Nav */}
              <div className="mt-10 flex items-center gap-3">
                <button
                  onClick={goPrev}
                  data-testid="testimonial-prev-btn"
                  aria-label="Previous testimonial"
                  className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-700 hover:border-[#0A2540] hover:text-[#0A2540] transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" strokeWidth={2.25} />
                </button>
                <button
                  onClick={goNext}
                  data-testid="testimonial-next-btn"
                  aria-label="Next testimonial"
                  className="w-10 h-10 rounded-full bg-[#0A2540] flex items-center justify-center text-white hover:bg-[#0F172A] transition-colors"
                >
                  <ChevronRight className="w-4 h-4" strokeWidth={2.25} />
                </button>
                <div className="ml-2 text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500">
                  {String(idx + 1).padStart(2, "0")} / {String(TESTIMONIALS.length).padStart(2, "0")}
                </div>
              </div>
            </div>

            {/* Stat side */}
            <div className="lg:col-span-4 bg-[#0A2540] text-white p-8 md:p-12 lg:p-16 flex flex-col justify-center relative overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div
                  key={idx}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="relative"
                >
                  <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-400 mb-3">
                    Project outcome
                  </div>
                  <div className="font-display text-5xl md:text-6xl font-semibold leading-none">
                    {current.stat}
                  </div>
                  <div className="mt-3 text-sm text-slate-300 font-mono uppercase tracking-widest">
                    {current.statLabel}
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Dots */}
              <div className="relative mt-10 flex items-center gap-2">
                {TESTIMONIALS.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIdx(i)}
                    data-testid={`testimonial-dot-${i}`}
                    aria-label={`Show testimonial ${i + 1}`}
                    className={`h-1.5 rounded-full transition-all ${
                      i === idx ? "w-8 bg-white" : "w-1.5 bg-white/30 hover:bg-white/60"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
